<template>
	<div data-component="edit-bot-cards-modal">
		<div data-element="form">
			<opening-lead-select-field
				:deal="deal"
				:excludeCards="getExcludedCardsForOpeningLead"
				v-model="openingLead"
			/>
		</div>
		<div data-element="cards-list">
			<div data-element="table-label">
				Cards to be played until student wins their first trick
			</div>
			<validation-observer
				data-element="form"
				tag="form"
				ref="form"
			>
				<bot-tricks-table
					v-for="(condition, key) in getConditions"
					:key="key"
					:index="key"
					:deal="deal"
					:condition="condition"
					:prev-condition="getPrevCondition(key)"
					:opening-lead="openingLead"
					@onCardSelection="handleCardSelection"
					@onClear="handleClear"
					@onDelete="handleDelete"
					@onAddTrick="handleAddTrick"
				/>
			</validation-observer>
		</div>
		<actions
			:actions="getActions"
			:isWaiting="isWaiting"
			:isDisabled="getIsDisabled"
			@actionClick="onActionClick"
		/>
	</div>
</template>

<script>

	import OpeningLeadSelectField from '@/components/dealLibrary/OpeningLeadSelectField';
	import BotTricksTable from '@/components/dealLibrary/BotTricksTable';
	import Actions from '@/components/ui/Actions';
	import api from '@/services/api';
	import actionClick from '@/mixins/actionClick';
	import invalidFormMessage from '@/mixins/invalidFormMessage';
	import { uppercaseObjectKeys } from '@/helpers';
	import { partnerships, SEATS, uppercaseAlphabet } from '@/consts';
	import { Game } from '@bridge128/bridge128-game-logic-lib';

	export default {
		components: {
			BotTricksTable,
			OpeningLeadSelectField,
			Actions
		},
		mixins: [actionClick, invalidFormMessage],
		props: {
			deal: {
				type: Object,
				default: undefined
			}
		},
		data: () => ({
			openingLead: undefined,
			isWaiting: undefined,
			conditions: []
		}),
		computed: {
			getGameData () {
				if (this.deal) {
					return Game.fromState(this.deal.deal);
				}
				return null;
			},
			getDummyPlayer () {
				if (this.getGameData) {
					return this.getGameData.dummy;
				}
				return null;
			},
			getDeclarer () {
				return this.deal?.declarer;
			},
			getDefenders () {
				if (!this.getDeclarer) {
					return false;
				}
				const defenders = partnerships
					.find((partnership) => {
						return !partnership.includes(this.getDeclarer);
					})
					.map(seat => SEATS[seat]);

				const openerSide = SEATS[this.getDeclarer].left;

				if (defenders[0]?.short !== openerSide) {
					const opener = defenders[1];
					defenders[1] = defenders[0];
					defenders[0] = opener;
				}

				return defenders;
			},
			getFirstDefenderSide () {
				if (this.getDefenders?.length) {
					return this.getDefenders[0]?.short;
				}
				return undefined;
			},
			getSecondDefenderSide () {
				if (this.getDefenders?.length) {
					return this.getDefenders[1]?.short;
				}
				return undefined;
			},
			getConditions () {
				if (this.openingLead && this.conditions?.length) {
					return this.conditions.map(item => {
						const [firstTrick, ...rest] = item;
						return [
							{
							  ...firstTrick,
								[this.getFirstDefenderSide]: this.openingLead
							},
							...rest
						];
					});
				}
				return [];
			},
			getIsDisabled  () {
				if (!this.conditions?.length) {
					return 'addCondition';
				} else if (this.conditions.length === uppercaseAlphabet.length) {
					return 'addCondition';
				}
				const lastIndex = this.conditions.length - 1;
				const lastCondition = this.conditions[lastIndex];
				const filteredLastCondition = this.filterCondition(lastCondition);
				if (!filteredLastCondition?.length) {
					return 'addCondition';
				}
				return undefined;
			},
			getExcludedCardsForOpeningLead () {
				return this.conditions
					.flat()
					.filter((card, index) => {
						return index % 2 === 1;
					})
					.map(card => card.name);
			},
			getFilteredConditions () {
				if (this.conditions?.length) {
					return this.conditions.map((item) => {
						return this.filterCondition(item);
					}).filter(item => item.length);
				}
				return [];
			},
			getActions () {
				return {
					primary: [
						{
							text: 'Save',
							btnId: 'save',
							actionId: 'handleSave'
						},
						{
							text: 'Add Condition',
							btnId: 'addCondition',
							actionId: 'handleAddCondition'
						}
					],
					secondary: [
						{
							text: 'Cancel',
							actionId: 'close'
						}
					]
				};
			}
		},
		watch: {
			'deal.openingLead': {
				handler (value) {
					this.openingLead = value;
				},
				immediate: true
			},
			openingLead (value, oldValue) {
				if (value && !oldValue) {
					this.conditions = [
						[
							{
								condition: [],
								[this.getFirstDefenderSide]: undefined,
								[this.getSecondDefenderSide]: undefined
							}
						]
					];
				}
				if (!value && oldValue) {
					this.conditions = [];
				}
			}
		},
		async created () {
			await this.fetchCards();
		},
		methods: {
			close () {
				this.$emit('close');
			},
			filterCondition (item = []) {
				return item.map(trick => {
					const { condition, ...sides } = trick;
					const filteredSides = Object.fromEntries(
						Object.entries(sides || {}).filter(([_key, value]) => value)
					);
					if (condition?.length) {
						return {
							...filteredSides,
							condition
						};
					}
					return filteredSides;
				}).filter(trick => Object.keys(trick).length);
			},
			getPrevCondition (index) {
				if (index === 0) {
					return undefined;
				}
				return this.getConditions[index - 1];
			},
			handleCardSelection (conditionIndex, trickIndex, card, column) {
				const key = this.getDummyPlayer?.name === column.short ? 'condition' : column.short;
				this.$set(this.conditions[conditionIndex][trickIndex], key, card);
			},
			handleAddTrick (conditionIndex) {
				const condition = this.conditions[conditionIndex];
				this.$set(this.conditions, conditionIndex, [
					...condition,
					{
						condition: [],
						[this.getFirstDefenderSide]: undefined,
						[this.getSecondDefenderSide]: undefined
					}
				]);
			},
			handleAddCondition () {
				if (this.conditions?.length) {
					this.conditions = [
						...this.conditions,
						[
							{
								condition: [],
								[this.getSecondDefenderSide]: undefined
							}
						]
					];
				}
			},
			capitalizeSides (conditions) {
				return conditions.map(item => {
					return item.map(trick => {
						const { condition, ...sides } = trick;
						const uppercaseSides = uppercaseObjectKeys(sides || {});
						if (condition) {
							return {
								condition,
								...uppercaseSides
							};
						}
						return uppercaseSides;
					});
				});
			},
			populateCards (conditions) {
				if (conditions?.length === 0 && this.deal?.openingLead) {
					this.conditions = [
						[
							{
								condition: [],
								[this.getSecondDefenderSide]: undefined
							}
						]
					];
				} else {
					this.conditions = this.capitalizeSides(conditions);
				}
			},
			async fetchCards () {
				const response = await api.deals.getDealPredefinedCards({
					dealId: this.$route.params.dealId
				});

				if (response) {
					this.populateCards(response);
				} else {
					console.error('Something went wrong. Empty response received.');
				}
			},
			async updateDeal () {
				const deal = await api.deals.updateDeal({
					dealId: this.$route.params.dealId,
					deal: {
						...this.deal,
						openingLead: this.openingLead
					}
				});
				if (this.deal && deal) {
					this.deal.openingLead = deal.openingLead;
					return true;
				} else {
					console.error('Something went wrong. Empty response received.');
				}
			},
			async updateBotCards () {
				const cards = await api.deals.updateDealPredefinedCards({
					dealId: this.$route.params.dealId,
					cards: this.getFilteredConditions
				});
				if (cards) {
					this.populateCards(cards);
					return true;
				} else {
					console.error('Something went wrong. Empty response received.');
				}
			},
			async handleSave () {
				const valid = await this.$refs.form.validate();
				if (!valid) {
					return;
				}

				this.isWaiting = 'save';

				const dealResult = await this.updateDeal();
				const cardsResult = await	this.updateBotCards();

				if (dealResult && cardsResult) {
					this.$store.commit('ui/showNotification', {
						notification: 'Successfully saved'
					});
				}
				this.isWaiting = undefined;
			},
			handleClear (conditionIndex, trickIndex) {
				this.conditions = this.conditions.map((condition, i) => {
					if (conditionIndex === i) {
						return condition.map((trick, k) => {
							if (trickIndex === k) {
								return {
									...(trickIndex === 0 ? {} : { [this.getFirstDefenderSide]: undefined }),
									[this.getSecondDefenderSide]: undefined,
									condition: []
								};
							}

							return trick;
						});
					}
					return condition;
				});
			},
			handleDelete (conditionIndex, trickIndex) {
				if (conditionIndex === 0) {
					if (trickIndex !== 0) {
						const condition = this.conditions[0];
						this.$set(this.conditions, 0, condition.filter((_, i) => trickIndex !== i));
					}
				} else {
					if (trickIndex === 0) {
						this.conditions = this.conditions.filter((_, i) => conditionIndex !== i);
					} else {
						const condition = this.conditions[conditionIndex];
						this.$set(this.conditions, conditionIndex, condition.filter((_, i) => trickIndex !== i));
					}
				}
			}
		}
	};
</script>

<style lang="scss" scoped>

	[data-component='edit-bot-cards-modal'] {
		[data-element='form'] {
			margin: 0;

			::v-deep [data-component='opening-lead-select'] {
				[data-component='select-field'] {
					margin-top: 0;
				}
			}
		}

		[data-element='cards-list'] {
			margin-bottom: rem(28);

			[data-element='table-label'] {
				display: block;
				font-size: rem(18);
				line-height: 1;
				margin-bottom: rem(12);
				font-weight: 700;

				&[data-required] {
					&:after {
						margin: 0;
						content: '*';
						display: inline;
						color: $c-red;
					}
				}

				&[data-error] {
					color: $c-red;
				}
			}

		}

		::v-deep [data-component='actions'] {
			padding: 0 0 rem(10) 0;

      @media (max-width: 1279px) {
        flex-direction: column;
        gap: 12px;

        [data-element="actions-primary"] {
          margin: 0;
          width: 100%;
          justify-content: space-between;

          [data-component="btn"] {
            margin: 0
          }
        }

        [data-element="actions-secondary"] {
          margin: 0;
          width: 100%;
          justify-content: space-between;

          [data-component="btn"] {
            margin: 0;
            width: 100%
          }
        }
      }
		}
	}

</style>
